
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function EmploymentApplicationPage() {
  useEffect(() => {
    // Update page title and meta description
    document.title = 'Employment Application - Join OED Glamping Team in Alabama';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Apply for employment at OED Glamping in Talladega, Alabama. Submit your application for careers in glamping hospitality, project management, sustainability, and operations. Join our growing eco-luxury tourism team.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'OED Glamping application, Alabama job application, glamping employment, hospitality jobs Talladega, eco-tourism careers, outdoor hospitality employment');
    }
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    position: '',
    department: '',
    availableStartDate: '',
    desiredSalary: '',
    employmentType: '',
    workAuthorization: '',
    previousEmployer: '',
    previousPosition: '',
    previousDuration: '',
    previousSalary: '',
    reasonForLeaving: '',
    education: '',
    degree: '',
    graduationYear: '',
    skills: '',
    certifications: '',
    experience: '',
    whyJoin: '',
    availability: '',
    references: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSubmit.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/submit/employment-application', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          position: '',
          department: '',
          availableStartDate: '',
          desiredSalary: '',
          employmentType: '',
          workAuthorization: '',
          previousEmployer: '',
          previousPosition: '',
          previousDuration: '',
          previousSalary: '',
          reasonForLeaving: '',
          education: '',
          degree: '',
          graduationYear: '',
          skills: '',
          certifications: '',
          experience: '',
          whyJoin: '',
          availability: '',
          references: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const jobPositions = [
    'Senior Project Manager',
    'Hospitality Operations Manager',
    'Sustainability Coordinator',
    'Investment Relations Specialist',
    'Site Maintenance Technician',
    'Guest Experience Coordinator',
    'Other'
  ];

  const departments = [
    'Development',
    'Operations',
    'Environmental',
    'Finance',
    'Maintenance',
    'Hospitality',
    'Other'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <header>
        <section 
          className="relative h-64 bg-cover bg-center bg-no-repeat flex items-center justify-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/ab2cbe96d2010d50300a6551aba00d67.jpg')`
          }}
        >
          <div className="text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-4xl font-bold mb-4">Employment Application</h1>
            <p className="text-lg">
              Join our team and help create extraordinary glamping experiences
            </p>
          </div>
        </section>
      </header>

      {/* Application Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Form</h2>
            <p className="text-gray-600">
              Please fill out all required fields. We'll review your application and contact you within 5-7 business days.
            </p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-emerald-600 text-xl mr-3"></i>
                <div>
                  <h3 className="text-emerald-800 font-semibold">Application Submitted Successfully!</h3>
                  <p className="text-emerald-700">Thank you for your interest. We'll review your application and contact you soon.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
                <div>
                  <h3 className="text-red-800 font-semibold">Submission Error</h3>
                  <p className="text-red-700">There was an error submitting your application. Please try again.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} id="employment-application" data-readdy-form>
            {/* Personal Information */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
              </div>
            </section>

            {/* Address Information */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Address Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code *
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
              </div>
            </section>

            {/* Position Information */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Position Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                    Position Applied For *
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select Position</option>
                    {jobPositions.map((position) => (
                      <option key={position} value={position}>{position}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                    Department *
                  </label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select Department</option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="availableStartDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Available Start Date *
                  </label>
                  <input
                    type="date"
                    id="availableStartDate"
                    name="availableStartDate"
                    value={formData.availableStartDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="desiredSalary" className="block text-sm font-medium text-gray-700 mb-2">
                    Desired Salary
                  </label>
                  <input
                    type="text"
                    id="desiredSalary"
                    name="desiredSalary"
                    value={formData.desiredSalary}
                    onChange={handleInputChange}
                    placeholder="e.g., $50,000 or Negotiable"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="employmentType" className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Type *
                  </label>
                  <select
                    id="employmentType"
                    name="employmentType"
                    value={formData.employmentType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select Type</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Temporary">Temporary</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="workAuthorization" className="block text-sm font-medium text-gray-700 mb-2">
                    Work Authorization *
                  </label>
                  <select
                    id="workAuthorization"
                    name="workAuthorization"
                    value={formData.workAuthorization}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select Authorization</option>
                    <option value="US Citizen">US Citizen</option>
                    <option value="Permanent Resident">Permanent Resident</option>
                    <option value="Work Visa">Work Visa</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Employment History */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Employment History</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="previousEmployer" className="block text-sm font-medium text-gray-700 mb-2">
                    Most Recent Employer
                  </label>
                  <input
                    type="text"
                    id="previousEmployer"
                    name="previousEmployer"
                    value={formData.previousEmployer}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="previousPosition" className="block text-sm font-medium text-gray-700 mb-2">
                    Position Title
                  </label>
                  <input
                    type="text"
                    id="previousPosition"
                    name="previousPosition"
                    value={formData.previousPosition}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="previousDuration" className="block text-sm font-medium text-gray-700 mb-2">
                    Employment Duration
                  </label>
                  <input
                    type="text"
                    id="previousDuration"
                    name="previousDuration"
                    value={formData.previousDuration}
                    onChange={handleInputChange}
                    placeholder="e.g., Jan 2020 - Dec 2023"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="previousSalary" className="block text-sm font-medium text-gray-700 mb-2">
                    Previous Salary
                  </label>
                  <input
                    type="text"
                    id="previousSalary"
                    name="previousSalary"
                    value={formData.previousSalary}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="reasonForLeaving" className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Leaving
                  </label>
                  <textarea
                    id="reasonForLeaving"
                    name="reasonForLeaving"
                    value={formData.reasonForLeaving}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>
            </section>

            {/* Education */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Education</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-2">
                    Highest Level of Education *
                  </label>
                  <select
                    id="education"
                    name="education"
                    value={formData.education}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm pr-8"
                  >
                    <option value="">Select Education Level</option>
                    <option value="High School">High School</option>
                    <option value="Associate Degree">Associate Degree</option>
                    <option value="Bachelor's Degree">Bachelor's Degree</option>
                    <option value="Master's Degree">Master's Degree</option>
                    <option value="Doctorate">Doctorate</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="degree" className="block text-sm font-medium text-gray-700 mb-2">
                    Degree/Field of Study
                  </label>
                  <input
                    type="text"
                    id="degree"
                    name="degree"
                    value={formData.degree}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700 mb-2">
                    Graduation Year
                  </label>
                  <input
                    type="number"
                    id="graduationYear"
                    name="graduationYear"
                    value={formData.graduationYear}
                    onChange={handleInputChange}
                    min="1950"
                    max="2030"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                  />
                </div>
              </div>
            </section>

            {/* Skills and Experience */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Skills and Experience</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="skills" className="block text-sm font-medium text-gray-700 mb-2">
                    Relevant Skills *
                  </label>
                  <textarea
                    id="skills"
                    name="skills"
                    value={formData.skills}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="List your relevant skills, software proficiency, languages, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <div>
                  <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-2">
                    Certifications and Licenses
                  </label>
                  <textarea
                    id="certifications"
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    placeholder="List any relevant certifications, licenses, or professional credentials"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <div>
                  <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
                    Relevant Work Experience *
                  </label>
                  <textarea
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Describe your relevant work experience and achievements"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>
            </section>

            {/* Additional Information */}
            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Additional Information</h3>
              <div className="space-y-6">
                <div>
                  <label htmlFor="whyJoin" className="block text-sm font-medium text-gray-700 mb-2">
                    Why do you want to join OED Glamping? *
                  </label>
                  <textarea
                    id="whyJoin"
                    name="whyJoin"
                    value={formData.whyJoin}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us what interests you about working with our company"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <div>
                  <label htmlFor="availability" className="block text-sm font-medium text-gray-700 mb-2">
                    Availability and Schedule Preferences
                  </label>
                  <textarea
                    id="availability"
                    name="availability"
                    value={formData.availability}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Describe your availability, preferred schedule, willingness to travel, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <div>
                  <label htmlFor="references" className="block text-sm font-medium text-gray-700 mb-2">
                    Professional References
                  </label>
                  <textarea
                    id="references"
                    name="references"
                    value={formData.references}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    placeholder="Provide contact information for 2-3 professional references (Name, Title, Company, Phone, Email)"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    rows={3}
                    maxLength={500}
                    placeholder="Any additional information you'd like us to know"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
              </div>
            </section>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <Link 
                to="/careers" 
                className="text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
              >
                ← Back to Careers
              </Link>
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
